import React from 'react';
import type { PressableProps, ViewStyle } from 'react-native';
import { Pressable, StyleSheet } from 'react-native';
import { Colors } from '../constants/Colors';
import { useDynamicStyles } from '../hooks';
import type { WithResponsive } from '../utils/responsivity';
import { TextReg } from './StyledText';

type Props = {
  id?: string;
  name: string;
  isVisible?: boolean;
  showBackground?: boolean;
  isLoading?: boolean;
  style?: ViewStyle;
  onPress?: (id?: string) => void;
  type?: 'tag' | 'category'; // New prop to distinguish between tags and categories
} & Omit<PressableProps, 'style' | 'onPress'>;

const TAG_COLORS = [
  Colors.purple1opacity50,
  Colors.yellow0opacity50,
  Colors.pink0opacity50,
];

export function Tag({
  id,
  name,
  isVisible = true,
  style,
  disabled,
  isLoading = false,
  showBackground = true,
  type,
  onPress,
  ...pressableProps
}: Props) {
  const styles = useDynamicStyles(createStyles, {
    id,
    isVisible,
    isLoading,
    showBackground,
    type,
  });

  return (
    <Pressable
      style={[styles.tagPill, style]}
      disabled={disabled || !onPress}
      onPress={() => {
        onPress?.(id);
      }}
      {...pressableProps}
    >
      <TextReg style={styles.tagText}>{name}</TextReg>
    </Pressable>
  );
}

const createStyles = ({
  id,
  isVisible,
  isLoading,
  showBackground,
  type,
}: WithResponsive<Props>) =>
  StyleSheet.create({
    tagPill: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 25,
      backgroundColor: showBackground
        ? isLoading
          ? Colors.opacity20
          : getTagColor(id, type)
        : 'transparent',
      opacity: isVisible ? 1 : 0,
      justifyContent: 'center',
    },
    tagText: {
      fontSize: 12,
    },
  });

// Simple string hash function to generate a deterministic number from a string
const hashString = (str?: string): number => {
  if (!str) return 0;

  // Using a prime multiplier (37) helps distribute values better
  // This approach works well for distributing small sets across limited buckets
  let hash = 0;
  const prime = 37;

  for (let i = 0; i < str.length; i++) {
    // Use prime number multiplication to create more variance
    hash = (hash * prime + str.charCodeAt(i)) % 997; // 997 is prime
  }

  return hash;
};

// Get a color based on the type (tag or category)
const getTagColor = (id?: string, type?: 'tag' | 'category'): string => {
  // If type is provided, use specific colors
  if (type === 'tag') {
    return Colors.opacity20;
  }
  if (type === 'category') {
    return Colors.yellow0opacity50;
  }

  // Fall back to the hash-based color for backward compatibility
  const colorIndex = hashString(id) % TAG_COLORS.length;
  return TAG_COLORS[colorIndex];
};
